import React, { TouchEvent as ReactTouchEvent, useCallback, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import LazyHydrate from 'react-lazy-hydration'
import { useMediaQuery } from 'react-responsive'
import { LinkButton } from '@/atoms/Button'
import { CloseIcon } from '@/atoms/Icons/CloseIcon'
import { HeadingXS, LabelLG, ParagraphSM } from '@/atoms/Text'
import { paths, slugs } from '@/constants'
import { useRegionContext } from '@/contexts/RegionContext'
import { ExperimentVariation, GBFadeInExperiment } from '@/experimentation'
import { EmbeddedVideo } from '@/molecules/EmbeddedVideo'
import { Modal } from '@/molecules/Modal'
import { SimpleThumbnail } from '@/molecules/Thumbnail'
import { useBotDetection } from '@/services/BotDetection'
import { useGuildMember } from '@/services/GuildUserService'
import { isUnitedStates } from '@/services/UserRegionService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { getBooleanFromLocalStorage, writeToLocalStorage } from '@/utils/local-storage'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'

export const AnnouncementModal: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const minSwipeDistance = 250
  const ref = useRef<HTMLDivElement | null>(null)
  const { isGuildMember, loading } = useGuildMember()
  const { t } = useTranslate('common')
  const { region } = useRegionContext()
  const { isBot } = useBotDetection()
  const isUS = isUnitedStates(region)
  const isDesktopOrLaptop = useMediaQuery({ query: '(min-width: 768px)' })

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false)
    writeToLocalStorage('hasSeenKingOfKingsModal', true)
  }, [])

  useEffect(() => {
    // Check if current date is after the cutoff date (June 23, 2025)
    const currentDate = new Date()
    const cutoffDate = new Date('2025-06-23T23:59:59')

    // Don't show after the 24th
    if (currentDate > cutoffDate) {
      return
    }

    const hasSeenModal = getBooleanFromLocalStorage('hasSeenKingOfKingsModal')

    if (!hasSeenModal) {
      setIsModalOpen(true)
    }
  }, [])

  const onTouchStart = useCallback((event: ReactTouchEvent) => {
    setTouchEnd(0)
    setTouchStart(event.targetTouches[0].clientY)
  }, [])

  const onTouchMove = useCallback((event: ReactTouchEvent) => setTouchEnd(event.targetTouches[0].clientY), [])

  const onTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return
    const html = window.document.getElementsByTagName('html')[0]
    html.style.overflow = 'hidden'
    const distance = touchStart - touchEnd
    const isSwipeDown = distance < -minSwipeDistance
    if (isModalOpen && isSwipeDown) {
      handleModalClose()
      html.style.overflow = 'auto'
    }
  }, [handleModalClose, isModalOpen, touchStart, touchEnd])

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (isModalOpen && ref.current && !ref.current.contains(event.target as Node)) {
        handleModalClose()
      }
    },
    [ref, handleModalClose, isModalOpen],
  )

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('touchstart', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('touchstart', handleClickOutside)
    }
  }, [handleClickOutside])

  useEffect(() => {
    if (isModalOpen) {
      setIsVisible(true)
    } else {
      setIsVisible(false)
    }
  }, [isModalOpen])

  if (!isUS || isBot) return null

  return (
    <LazyHydrate whenVisible>
      <>
        <When condition={isDesktopOrLaptop}>
          <Modal
            isOpen={isModalOpen}
            onClose={handleModalClose}
            panelClasses="md:min-w-[88vw] lg:min-w-min !p-4 !pb-8 !max-w-[834px]"
            className=""
          >
            <AnnouncementModalBody
              isGuildMember={isGuildMember}
              t={t}
              handleModalClose={handleModalClose}
              loading={loading}
            />
          </Modal>
        </When>
        <When condition={!isDesktopOrLaptop}>
          {isVisible && (
            <div
              className={classNames(
                'fixed inset-0 z-[1999]  backdrop-opacity-70 transition-all',
                isVisible ? 'bg-core-gray-950/70' : 'bg-transparent',
              )}
            />
          )}
          <div
            ref={ref}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className={classNames(
              'box-border fixed bottom-0 left-0 z-[2000] max-h-[90vh] w-full rounded-t-3xl border-t bg-core-gray-100 transition-all duration-300 ease-in-out',
              'sm:max-w-[400px] sm:right-0 sm:mx-auto sm:rounded-b-3xl sm:overflow-hidden sm:h-fit',
              isVisible
                ? 'translate-y-0 sm:my-auto sm:bottom-0 sm:top-0 sm:opacity-1'
                : 'translate-y-full sm:opacity-0',
            )}
          >
            <div className="p-4 pb-8">
              <AnnouncementModalBody
                isGuildMember={isGuildMember}
                t={t}
                handleModalClose={handleModalClose}
                loading={loading}
              />
            </div>
          </div>
        </When>
      </>
    </LazyHydrate>
  )
}

interface AnnouncementModalBodyProps {
  isGuildMember: boolean
  loading: boolean
  t: TranslateFunction
  handleModalClose: () => void
}

const AnnouncementModalBody: React.FC<AnnouncementModalBodyProps> = ({
  isGuildMember,
  loading,
  t,
  handleModalClose,
}) => {
  const track = useSafeTrack()
  const [loadPlayer, setLoadPlayer] = useState(false)

  const handleClick = useCallback(() => {
    track('Announcement Modal CTA Clicked', isGuildMember)
    handleModalClose()
  }, [handleModalClose, isGuildMember, track])

  const handleLoadPlayer = useCallback(() => {
    setLoadPlayer((prev) => !prev)
  }, [])

  return (
    <div className="flex flex-col text-black">
      <div className="flex items-center justify-end">
        <div onClick={handleModalClose} className="cursor-pointer">
          <CloseIcon color="black" size={24} className="cursor-pointer" />
        </div>
      </div>
      <div className="flex flex-col gap-y-4 md:gap-y-6 md:px-6">
        <HeadingXS weight="bold" className="md:photon-heading-sm xl:photon-heading-md text-pretty">
          {t('watchGreatestStoryEverTold', `Watch the greatest story ever told at home today!`)}
        </HeadingXS>
        <When condition={!loadPlayer}>
          <SimpleThumbnail
            imageUrl={getCloudinaryImageUrl({
              path: '/v1748456653/angel-studios/projects/king-of-kings/16x9.webp',
              height: 425,
              width: 754,
            })}
            showButton
            onClick={handleLoadPlayer}
            className={'rounded-2xl'}
          />
        </When>
        <When condition={loadPlayer}>
          <div className="overflow-hidden rounded-2xl">
            <EmbeddedVideo
              className="w-full"
              title={t('tkokTrailerNowStreaming', 'The King Of Kings | Now Streaming | Angel')}
              url={`https://www.youtube.com/embed/Y9kOoZbfauE?autoplay=1&si=t6PJP9JfF2pze-iV`}
            />
          </div>
        </When>
        <div className="flex flex-col items-center gap-y-4 text-center md:gap-y-6">
          <ParagraphSM color="core-gray-700" className={classNames('max-w-[670px]')}>
            <Translate t={t} i18nKey="kingOfKingsAnnouncementModalDescription">
              Gather your family for the most important story ever told. <i>The King of Kings</i> brings scripture to
              life in a way that’s engaging, emotional, and built for all ages to understand.
            </Translate>
          </ParagraphSM>
          <div className="min-h-[52px] w-full">
            {!loading && (
              <LazyHydrate whenVisible>
                <>
                  <When condition={isGuildMember}>
                    <GBFadeInExperiment experimentName="king-of-kings-announcement-guild" defaultValue="watch">
                      <ExperimentVariation variation={'watch'}>
                        <div className="flex w-full justify-center">
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={'/watch/king-of-kings/episode/69948321-6173-4d15-96da-fc652409f9a2'}
                            variant="oxide-primary"
                          >
                            <LabelLG>{t('watchNow', 'Watch Now')}</LabelLG>
                          </LinkButton>
                        </div>
                      </ExperimentVariation>
                      <ExperimentVariation variation={'watch-and-share'}>
                        <div className="flex min-h-[120px] w-full flex-col items-center gap-4 md:min-h-[52px] md:flex-row md:gap-6">
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={'/watch/king-of-kings/episode/69948321-6173-4d15-96da-fc652409f9a2?share=true'}
                            variant="core-gray-800"
                          >
                            <LabelLG>{t('shareFreeAccess', 'Share Free Access')}</LabelLG>
                          </LinkButton>
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={'/watch/king-of-kings/episode/69948321-6173-4d15-96da-fc652409f9a2'}
                            variant="oxide-primary"
                          >
                            <LabelLG>{t('watchNow', 'Watch Now')}</LabelLG>
                          </LinkButton>
                        </div>
                      </ExperimentVariation>
                      <ExperimentVariation default>
                        <div className="flex w-full justify-center">
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={'/watch/king-of-kings/episode/69948321-6173-4d15-96da-fc652409f9a2'}
                            variant="oxide-primary"
                          >
                            <LabelLG>{t('watchNow', 'Watch Now')}</LabelLG>
                          </LinkButton>
                        </div>
                      </ExperimentVariation>
                    </GBFadeInExperiment>
                  </When>
                  <When condition={!isGuildMember}>
                    <GBFadeInExperiment
                      experimentName="king-of-kings-announcement-non-guild"
                      defaultValue="join-to-watch"
                    >
                      <ExperimentVariation variation={'join-to-watch'}>
                        <div className="flex w-full justify-center">
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={`${paths.guild.join}/${slugs.kingOfKings}`}
                            variant="oxide-primary"
                          >
                            <LabelLG>{t('joinTheGuildToWatch', 'Join the Guild to Watch')}</LabelLG>
                          </LinkButton>
                        </div>
                      </ExperimentVariation>
                      <ExperimentVariation variation={'watch-sneak-peek'}>
                        <div className="flex w-full justify-center">
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={'/watch/king-of-kings/episode/7fcd4440-8b7e-45ac-b638-2abc81737a3c'}
                            variant="oxide-primary"
                          >
                            <LabelLG>{t('watchFirstEighteenMinutes', 'Watch the First 18 Minutes')}</LabelLG>
                          </LinkButton>
                        </div>
                      </ExperimentVariation>
                      <ExperimentVariation default>
                        <div className="flex w-full justify-center">
                          <LinkButton
                            className="w-full rounded-[14px] px-6 py-[17px] md:w-fit"
                            onClick={handleClick}
                            href={`${paths.guild.join}/${slugs.kingOfKings}`}
                            variant="oxide-primary"
                          >
                            <LabelLG>{t('joinTheGuildToWatch', 'Join the Guild to Watch')}</LabelLG>
                          </LinkButton>
                        </div>
                      </ExperimentVariation>
                    </GBFadeInExperiment>
                  </When>
                </>
              </LazyHydrate>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
